<template>
    <div class="path-bar">
        <button
            v-if="path.length"
            type="button"
            class="btn btn-ghost btn-icon-only path-bar-back"
            :aria-label="backLabel"
            :title="backLabel"
            @click="$emit('navigate', parentId)"
        ><i class="ti ti-arrow-left" aria-hidden="true"></i></button>

        <nav class="path-bar-nav" :aria-label="navLabel">
            <ol class="breadcrumb">
                <li
                    class="breadcrumb-item"
                    :class="[targetClass(null), { active: !path.length }]"
                    v-on="path.length ? dropHandlers(null) : {}"
                >
                    <a
                        ref="root"
                        :href="rootUrl"
                        class="path-bar-root"
                        :aria-label="rootLabel"
                        :title="rootLabel"
                        :aria-current="path.length ? null : 'page'"
                        @click="follow(null, $event)"
                    ><i class="ti ti-folders" aria-hidden="true"></i></a>
                </li>
                <li
                    v-for="(crumb, index) in path"
                    :key="crumb.id"
                    class="breadcrumb-item"
                    :class="[targetClass(crumb.id), { active: index === path.length - 1 }]"
                    :aria-current="index === path.length - 1 ? 'page' : null"
                    v-on="index < path.length - 1 ? dropHandlers(crumb.id) : {}"
                >
                    <a
                        v-if="index < path.length - 1"
                        :href="crumb.url"
                        :title="crumb.title"
                        @click="follow(crumb.id, $event)"
                    >{{ crumb.title }}</a>
                    <span
                        v-else
                        ref="current"
                        tabindex="-1"
                        :title="crumb.title"
                    >{{ crumb.title }}</span>
                </li>
            </ol>
        </nav>

        <slot name="end"></slot>
    </div>
</template>

<script>
import { i18n } from '@humhub/vue';

const plainClick = (event) => !(event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);

/**
 * The path of a hierarchical view (`<path-bar>`): Bootstrap's `.breadcrumb` as a grey bar, with
 * the root as an icon link, the ancestors as links and the current level (`.active`) as text, a
 * back button on narrow screens and an `end` slot on the right (e.g. a `SelectionMenu`).
 *
 * - `path`: the levels below the root, outermost first — `[{ id, title, url }]`; the last one
 *   is the current level. Empty at the top level, where the root is current.
 * - `rootLabel` (accessible name and tooltip of the root link), `rootUrl`.
 * - Emits `navigate(id)` for a plain click on the root (`id` = `null`), an ancestor or the back
 *   button (the parent level); a click with a modifier or the middle button is left to the
 *   browser, so the `url`s open in a new tab.
 * - Drop targets: the root (below the top level) and every ancestor, never the current level.
 *   `canDrop(id, event)` decides whether a drag may land there; for those it emits
 *   `drag-over(id, event)` (repeatedly, for as long as the drag hovers it), `drag-leave(id, event)`
 *   (once, when the pointer actually leaves the crumb rather than moving onto one of its children)
 *   and `drop-on(id, event)` (after `preventDefault()` and `stopPropagation()`, so a surrounding
 *   `DropZone` stays quiet). No `drag-leave` follows a `drop-on` — the owner is expected to clear
 *   its own drop target on drop too. `dropTargetId` highlights one (`null` = the root, `undefined`
 *   = none) — unlike `TileGrid`'s `dropTargetKey`, whose `null` means no target; an owner
 *   tracking drop targets across both keeps two separate values, not one shared `null`.
 * - `focusCurrent()` moves the focus to the current level (the root link at the top level) —
 *   for an owner whose focused control goes away, e.g. a `SelectionMenu` after "Clear
 *   selection" or a delete; the current level is focusable from script only (`tabindex="-1"`).
 * - Styling: `.path-bar` in `resources/scss/_item-browser.scss`; the back button shows at
 *   768px and below.
 *
 * @since 1.20
 */
export default {
    name: 'PathBar',
    props: {
        path: { type: Array, default: () => [] },
        rootLabel: { type: String, required: true },
        rootUrl: { type: String, required: true },
        canDrop: { type: Function, default: () => false },
        // No `type`: `dropTargetId` must keep `null` (the root) distinct from `undefined` (no
        // target, the default) - `type: [Number, String, null]` trips Vue's prop validator (it
        // treats `null` as "no type restriction" only when it is the *sole* type, not inside an
        // array) and can warn or coerce `null` back to the default.
        dropTargetId: { default: undefined },
    },
    emits: ['navigate', 'drag-over', 'drag-leave', 'drop-on'],
    computed: {
        parentId() {
            return this.path.length > 1 ? this.path[this.path.length - 2].id : null;
        },
        backLabel() {
            return i18n.t('base', 'Back');
        },
        navLabel() {
            return i18n.t('base', 'Breadcrumb');
        },
    },
    methods: {
        focusCurrent() {
            const target = this.path.length ? this.$refs.current : this.$refs.root;
            // A ref inside `v-for` is an array
            (Array.isArray(target) ? target[0] : target)?.focus();
        },
        follow(id, event) {
            if (!plainClick(event)) {
                return;
            }
            event.preventDefault();
            this.$emit('navigate', id);
        },
        targetClass(id) {
            return { 'is-drop-target': this.dropTargetId !== undefined && this.dropTargetId === id };
        },
        dropHandlers(id) {
            return {
                dragover: (event) => {
                    if (this.canDrop(id, event)) {
                        event.preventDefault();
                        this.$emit('drag-over', id, event);
                    }
                },
                dragleave: (event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        this.$emit('drag-leave', id, event);
                    }
                },
                drop: (event) => {
                    if (!this.canDrop(id, event)) {
                        return;
                    }
                    event.preventDefault();
                    event.stopPropagation();
                    this.$emit('drop-on', id, event);
                },
            };
        },
    },
};
</script>
